(() => {
  const drawer = () => document.getElementById('cart-drawer');
  let syncTimer;
  let layoutTimer;
  let checkoutBusy = false;

  function alignContentOrder() {
    const root = drawer();
    const scroller = root?.querySelector(':scope > .drawer__content');
    const flow = scroller?.querySelector(':scope > .cart-flow');
    const items = scroller?.querySelector(':scope > .cart-drawer__content');

    if (!scroller || !flow || !items) return;

    // Preserve a logical DOM reading order: cart items first, then the
    // utilities, add-ons and delivery modules already authored inside flow.
    // CSS may adapt their visual layout without confusing screen readers.
    if (flow.previousElementSibling !== items) {
      scroller.insertBefore(items, flow);
    }
  }

  function bindScrollState() {
    const root = drawer();
    const scroller = root?.querySelector(':scope > .drawer__content');
    if (!root || !scroller || scroller.dataset.orbitScrollBound === 'true') return;

    const update = () => {
      root.classList.toggle('cart-drawer--scrolled', scroller.scrollTop > 24);
    };

    scroller.dataset.orbitScrollBound = 'true';
    scroller.addEventListener('scroll', update, { passive: true });
    update();
  }

  function enhanceUpsellButtons() {
    drawer()?.querySelectorAll('.cart-upsell__product').forEach((card) => {
      const button = card.querySelector('[data-add-btn]');
      const title = card.querySelector('[data-title]')?.textContent?.trim();
      const action = button?.querySelector('[data-btn-text]')?.textContent?.trim();

      if (!button || !title) return;
      button.setAttribute('aria-label', `${action || '加入'} ${title}`);
    });
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'TWD';
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0
    }).format(cents / 100);
  }

  function setTotalLabel(kind) {
    const label = drawer()?.querySelector('[data-orbit-total-label]');
    if (!label) return;

    label.textContent = kind === 'grand'
      ? label.dataset.grandTotalLabel
      : label.dataset.subtotalLabel;
  }

  function syncCalculatedVibeTotal() {
    const root = drawer();
    const summary = root?.querySelector('[data-vibe-delivery="order-summary"]');
    const shipping = summary?.querySelector('[id^="cart-shipping-"]');
    const vibeTotal = summary?.querySelector('[id^="cart-total-"]');
    const orbitTotal = root?.querySelector('[data-orbit-total]');

    // Vibe initializes the total before a delivery rate exists. Only mirror it
    // after the shipping row contains a calculated amount, including free
    // shipping ($0), so the sticky footer never treats an estimate as final.
    if (!shipping || !vibeTotal || !orbitTotal || !/\d/.test(shipping.textContent || '')) {
      return false;
    }

    const value = vibeTotal.textContent?.trim();
    if (!value) return false;

    orbitTotal.textContent = value;
    setTotalLabel('grand');
    return true;
  }

  function syncCheckoutStatus() {
    const root = drawer();
    const status = root?.querySelector('.cart-orbit__checkout-copy small');
    if (!root || !status) return;

    const count = root.querySelector('.cart-drawer__count-badge')?.textContent?.trim() || '0';
    const activeMethod = root.querySelector('.vd-shipping-option.active');
    const methodDisplay = root.querySelector('.vd-method-display');
    const methodName = activeMethod?.querySelector('strong')?.textContent?.trim() ||
      root.querySelector('.vd-method-name')?.textContent?.trim();
    const methodSelected = methodName && (
      activeMethod ||
      (methodDisplay && getComputedStyle(methodDisplay).display !== 'none')
    );

    status.textContent = methodSelected
      ? `${count} 件 · ${methodName}`
      : `${count} 件 · 尚未選配送`;
  }

  async function syncTotal(cart) {
    const total = drawer()?.querySelector('[data-orbit-total]');
    if (!total) return;

    if (cart?.total_price != null) {
      total.textContent = formatMoney(cart.total_price);
      setTotalLabel('subtotal');
      syncCalculatedVibeTotal();
      return;
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) return;
      const currentCart = await response.json();
      total.textContent = formatMoney(currentCart.total_price);
      setTotalLabel('subtotal');
      syncCalculatedVibeTotal();
    } catch {
      return;
    }
  }

  function setCheckoutBusy(isBusy) {
    const button = drawer()?.querySelector('[data-orbit-checkout]');
    if (!button) return;

    const label = button.querySelector('[data-orbit-checkout-label]');
    const icon = button.querySelector('[data-orbit-checkout-icon]');
    const spinner = button.querySelector('[data-orbit-checkout-spinner]');

    checkoutBusy = isBusy;
    button.disabled = isBusy;
    button.setAttribute('aria-busy', String(isBusy));
    if (label) label.textContent = isBusy ? label.dataset.loadingLabel : label.dataset.defaultLabel;
    if (icon) icon.hidden = isBusy;
    if (spinner) spinner.hidden = !isBusy;
  }

  function syncLayout() {
    clearTimeout(layoutTimer);
    layoutTimer = setTimeout(() => {
      alignContentOrder();
      enhanceUpsellButtons();
      bindScrollState();
      syncCheckoutStatus();
    }, 40);
  }

  function sync(cart) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncLayout();
      syncTotal(cart);
    }, 60);
  }

  function revealDelivery() {
    const root = drawer();
    const delivery = root?.querySelector('.cart-flow__section--delivery');
    if (!delivery) return;

    delivery.classList.remove('cart-flow__section--attention');
    delivery.scrollIntoView({ behavior: 'smooth', block: 'start' });
    requestAnimationFrame(() => {
      delivery.classList.add('cart-flow__section--attention');
      delivery.querySelector('button:not([disabled])')?.focus({ preventScroll: true });
      setTimeout(() => delivery.classList.remove('cart-flow__section--attention'), 1600);
    });
  }

  document.addEventListener('click', (event) => {
    const proxy = event.target.closest('[data-orbit-checkout]');
    if (!proxy) return;

    if (checkoutBusy) return;

    const root = drawer();
    const checkout = root?.querySelector('.vibe-checkout-button');
    if (!checkout || checkout.disabled) {
      revealDelivery();
      return;
    }

    checkout.click();
    if (!checkoutBusy) revealDelivery();
  });

  document.addEventListener('vibe:checkout-start', () => setCheckoutBusy(true));
  document.addEventListener('vibe:checkout-ready', () => setCheckoutBusy(true));
  document.addEventListener('vibe:checkout-error', () => setCheckoutBusy(false));
  window.addEventListener('pageshow', () => setCheckoutBusy(false));

  [
    'cart:updated',
    'cart:refresh',
    'cart:item-added',
    'cart:rerender-items',
    'on:cart:add',
    'on:line-item:change',
    'dispatch:cart-drawer:refresh',
    'dispatch:cart-drawer:open'
  ].forEach((name) => {
    document.addEventListener(name, (event) => sync(event.detail?.cart));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync());
  } else {
    sync();
  }

  const observer = new MutationObserver((mutations) => {
    const vibeTotalChanged = mutations.some((mutation) => {
      const target = mutation.target instanceof Element
        ? mutation.target
        : mutation.target.parentElement;
      return target?.matches('[id^="cart-total-"]') ||
        target?.closest('[id^="cart-total-"]');
    });

    if (vibeTotalChanged) syncCalculatedVibeTotal();

    const deliveryStateChanged = mutations.some((mutation) => {
      const target = mutation.target instanceof Element
        ? mutation.target
        : mutation.target.parentElement;
      return target?.matches('.vd-method-display, .vd-method-name') ||
        target?.closest('.vd-method-display, .vd-method-name');
    });
    if (deliveryStateChanged) syncCheckoutStatus();

    const needsLayoutSync = mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      return !target?.closest('.cart-orbit__footer');
    });
    if (needsLayoutSync) syncLayout();
  });
  const startObserver = () => {
    const root = drawer();
    if (root) observer.observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver);
  } else {
    startObserver();
  }
})();
